// Ekspor bagan grup aktif ke PNG / PDF berkualitas laporan.
// Menggunakan ulang <g id="skt-scene"> yang sudah dirender (world coords), tanpa menduplikasi logika bagan.

const TOKENS = ["ink", "secondary", "muted", "page", "panel", "grid", "border", "edge", "red", "yellow", "info", "green", "focus"];

// Palet TERANG tetap untuk hasil ekspor (hemat tinta walau layar sedang gelap).
const LIGHT_THEME: Record<string, string> = {
  ink: "#1c2736", secondary: "#4a5668", muted: "#7b8596", page: "#ffffff", panel: "#ffffff",
  grid: "#e4e8ef", border: "#d3d9e2", edge: "#3b4a5e", red: "#b0244f", yellow: "#9a6a00",
  info: "#2d5f8a", green: "#2f7a4c", focus: "#2d5f8a",
};

function readTheme(): Record<string, string> {
  return { ...LIGHT_THEME };
}

interface ExportOpts {
  bbox?: { minX: number; minY: number; maxX: number; maxY: number };
  group: string;
  dateLabel: string;
  stepLabel?: string;
  hiddenOwners?: Set<string>;
  ownerNames?: Record<string, string>;
  categoryLegend?: { name: string; color: string }[];
  chainNodes?: Set<string>;
  chainEdges?: Set<string>;
}

function sceneBBox(scene: SVGGElement): { minX: number; minY: number; maxX: number; maxY: number } {
  try {
    const b = (scene as any).getBBox();
    return { minX: b.x, minY: b.y, maxX: b.x + b.width, maxY: b.y + b.height };
  } catch {
    return { minX: 0, minY: 0, maxX: 100, maxY: 100 };
  }
}

// Kumpulkan legenda pemilik (id, warna, nama) dari scene yang sudah dibersihkan.
function collectOwners(clone: SVGGElement, names?: Record<string, string>): { id: string; color: string; name: string }[] {
  const colorOf = new Map<string, string>();
  clone.querySelectorAll("[data-owner]").forEach((el) => {
    const id = el.getAttribute("data-owner");
    if (!id || colorOf.has(id)) return;
    const poly = el.querySelector("polygon");
    const c = poly?.getAttribute("fill") || el.querySelector("rect")?.getAttribute("stroke");
    if (c) colorOf.set(id, c);
  });
  return [...colorOf.entries()].map(([id, color]) => ({ id, color, name: names?.[id] ?? id }));
}

function buildSVG(opts: ExportOpts): { svg: string; w: number; h: number } {
  const scene = document.getElementById("skt-scene");
  if (!scene) throw new Error("Scene tidak ditemukan");
  const clone = scene.cloneNode(true) as SVGGElement;
  clone.removeAttribute("transform"); // pakai koordinat dunia asli

  // buang garis milik pemilik yang tidak dipilih untuk ekspor
  if (opts.hiddenOwners && opts.hiddenOwners.size) {
    clone.querySelectorAll("[data-owner]").forEach((el) => {
      const o = el.getAttribute("data-owner");
      if (o && opts.hiddenOwners!.has(o)) el.remove();
    });
  }

  // ekspor rantai tersorot saja: buang kartu & garis di luar rantai
  if (opts.chainNodes) {
    clone.querySelectorAll("[data-node]").forEach((el) => {
      const id = el.getAttribute("data-node");
      if (id && !opts.chainNodes!.has(id)) el.remove();
    });
    clone.querySelectorAll("[data-edge]").forEach((el) => {
      const id = el.getAttribute("data-edge");
      if (id && !(opts.chainEdges && opts.chainEdges.has(id))) el.remove();
    });
  }

  const legendOwners = collectOwners(clone, opts.ownerNames);
  const legendCats = opts.categoryLegend ?? [];
  const bbox = opts.bbox ?? sceneBBox(scene as unknown as SVGGElement);
  const t = readTheme();
  const pad = 60;
  const baseTitleH = opts.stepLabel ? 116 : 96;
  // ruang legenda (kolom kanan): pemilik + kategori
  const ownerRows = legendOwners.length ? legendOwners.length + 1 : 0;
  const catRows = legendCats.length ? legendCats.length + 1 : 0;
  const legendNeed = (ownerRows + catRows) ? 30 + (ownerRows + catRows) * 18 + 10 : 0;
  const titleH = Math.max(baseTitleH, legendNeed);
  const footerH = 46;
  const contentW = bbox.maxX - bbox.minX;
  const contentH = bbox.maxY - bbox.minY;
  const w = Math.max(700, contentW + pad * 2);
  const h = contentH + titleH + pad + footerH;
  const tx = pad - bbox.minX;
  const ty = titleH + (pad / 2) - bbox.minY;

  const style = `
    svg{${TOKENS.map((k) => `--${k}:${t[k]}`).join(";")}}
    text{font-family:'Public Sans',system-ui,sans-serif}
    .serif{font-family:'Source Serif 4',Georgia,serif;font-weight:600}
    .tnum{font-variant-numeric:tabular-nums}
  `;
  const now = new Date();
  const ts = now.toLocaleString("id-ID", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });

  const inner = new XMLSerializer().serializeToString(clone);

  const stepLine = opts.stepLabel
    ? `<text x="${pad}" y="94" font-size="13" fill="var(--ink)" class="serif">${escapeXml(opts.stepLabel)}</text>`
    : "";

  // Legenda (kolom kanan atas): pemilik lalu kategori
  const parts: string[] = [];
  const lx = w - pad - 230;
  let ly = 22;
  if (legendOwners.length) {
    parts.push(`<text x="${lx}" y="${ly}" font-size="12" fill="var(--secondary)" style="font-weight:700">Warna = pemilik</text>`);
    ly += 18;
    for (const o of legendOwners) {
      parts.push(
        `<rect x="${lx}" y="${ly - 10}" width="24" height="8" rx="4" fill="${escapeXml(o.color)}"/>` +
        `<text x="${lx + 32}" y="${ly}" font-size="12" fill="var(--ink)">${escapeXml(o.name)}</text>`
      );
      ly += 18;
    }
  }
  if (legendCats.length) {
    ly += 4;
    parts.push(`<text x="${lx}" y="${ly}" font-size="12" fill="var(--secondary)" style="font-weight:700">Kategori PT</text>`);
    ly += 18;
    for (const c of legendCats) {
      parts.push(
        `<rect x="${lx}" y="${ly - 11}" width="14" height="11" rx="3" fill="${escapeXml(c.color)}"/>` +
        `<text x="${lx + 22}" y="${ly}" font-size="12" fill="var(--ink)">${escapeXml(c.name)}</text>`
      );
      ly += 18;
    }
  }
  const legendSvg = parts.join("");

  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}" viewBox="0 0 ${w} ${h}">` +
    `<style>${style}</style>` +
    `<rect x="0" y="0" width="${w}" height="${h}" fill="var(--page)"/>` +
    `<text x="${pad}" y="46" class="serif" font-size="26" fill="var(--ink)">STRUKTURKU</text>` +
    `<text x="${pad}" y="70" font-size="13" fill="var(--secondary)">Grup klien: ${escapeXml(opts.group)} — Struktur per ${escapeXml(opts.dateLabel)}</text>` +
    stepLine +
    legendSvg +
    `<line x1="${pad}" y1="${titleH - 12}" x2="${w - pad}" y2="${titleH - 12}" stroke="var(--border)" stroke-width="1"/>` +
    `<g transform="translate(${tx} ${ty})">${inner}</g>` +
    `<text x="${pad}" y="${h - 18}" font-size="11" fill="var(--muted)">STRUKTURKU • Abadi Premium Consultant • ${escapeXml(ts)}</text>` +
    `</svg>`;
  return { svg, w, h };
}

function escapeXml(s: string): string {
  return (s || "").replace(/[<>&'"]/g, (c) => ({ "<": "&lt;", ">": "&gt;", "&": "&amp;", "'": "&apos;", '"': "&quot;" }[c] as string));
}

function rasterize(svg: string, w: number, h: number, scale: number): Promise<string> {
  return new Promise((resolve, reject) => {
    const blob = new Blob([svg], { type: "image/svg+xml;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(w * scale);
      canvas.height = Math.round(h * scale);
      const ctx = canvas.getContext("2d")!;
      ctx.scale(scale, scale);
      ctx.drawImage(img, 0, 0);
      URL.revokeObjectURL(url);
      resolve(canvas.toDataURL("image/png"));
    };
    img.onerror = (e) => { URL.revokeObjectURL(url); reject(e); };
    img.src = url;
  });
}

function triggerDownload(dataUrl: string, filename: string) {
  const a = document.createElement("a");
  a.href = dataUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
}

function slug(s: string): string {
  return (s || "grup").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "") || "grup";
}

// Logo STRUKTURKU (vektor) untuk PDF: tiga batang menaik. s = tinggi batang tertinggi (pt).
function drawLogoVec(pdf: any, x: number, y: number, s: number) {
  const bw = s * 0.26, gap = s * 0.11, r = s * 0.06;
  pdf.setFillColor(47, 107, 237); pdf.roundedRect(x, y + s * 0.55, bw, s * 0.45, r, r, "F");
  pdf.setFillColor(143, 180, 245); pdf.roundedRect(x + bw + gap, y + s * 0.28, bw, s * 0.72, r, r, "F");
  pdf.setFillColor(20, 41, 74); pdf.roundedRect(x + 2 * (bw + gap), y, bw, s, r, r, "F");
}

// Kop laporan bermerk STRUKTURKU. Mengembalikan posisi y berikutnya.
function pdfBrandHeader(pdf: any, W: number, M: number, opts: { title: string; group?: string }): number {
  let y = M;
  drawLogoVec(pdf, M, y, 22);
  pdf.setFont("helvetica", "bold"); pdf.setFontSize(15); pdf.setTextColor(20, 41, 74);
  pdf.text("STRUKTURKU", M + 40, y + 9);
  pdf.setFont("helvetica", "normal"); pdf.setFontSize(8.5); pdf.setTextColor(120, 128, 138);
  pdf.text("by Abadi Premium Consultant", M + 40, y + 19);
  pdf.setFont("helvetica", "bold"); pdf.setFontSize(13); pdf.setTextColor(20, 30, 40);
  pdf.text(opts.title, W - M, y + 8, { align: "right" });
  if (opts.group) {
    pdf.setFont("helvetica", "normal"); pdf.setFontSize(9.5); pdf.setTextColor(90, 100, 110);
    pdf.text(`Grup klien: ${opts.group}`, W - M, y + 20, { align: "right" });
  }
  y += 34;
  pdf.setDrawColor(20, 41, 74); pdf.setLineWidth(1.4); pdf.line(M, y, W - M, y);
  pdf.setLineWidth(0.2);
  return y + 16;
}

function pdfBrandFooter(pdf: any, W: number, H: number, M: number, note?: string) {
  const now = new Date().toLocaleString("id-ID", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  const pages = pdf.getNumberOfPages();
  pdf.setFont("helvetica", "normal"); pdf.setFontSize(8.5); pdf.setTextColor(140, 148, 158);
  for (let i = 1; i <= pages; i++) {
    pdf.setPage(i);
    pdf.text(`STRUKTURKU · Abadi Premium Consultant · ${now}${note ? " · " + note : ""}`, M, H - 20);
    pdf.text(`Hal. ${i}/${pages}`, W - M, H - 20, { align: "right" });
  }
}

export interface ClientSummaryData {
  group: string;
  dateLabel: string;
  stats: { companies: number; documents: number; issues: number; completePct: number };
  companies: { name: string; kedudukan: string; status: "ok" | "yellow" | "red"; shareholders: number; docsMissing: number }[];
  ubo: string[];
}

export async function exportClientSummary(d: ClientSummaryData): Promise<void> {
  const { jsPDF } = await import("jspdf");
  const pdf = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4", compress: true });
  const W = pdf.internal.pageSize.getWidth();
  const H = pdf.internal.pageSize.getHeight();
  const M = 40;
  let y = pdfBrandHeader(pdf, W, M, { title: "Ringkasan Klien" });

  pdf.setFont("times", "bold"); pdf.setFontSize(22); pdf.setTextColor(20, 41, 74);
  pdf.text(d.group, M, y + 6); y += 14;
  pdf.setFont("helvetica", "normal"); pdf.setFontSize(10); pdf.setTextColor(110, 118, 128);
  pdf.text(`Struktur per ${d.dateLabel}`, M, y + 6); y += 26;

  // Kartu statistik
  const cards = [
    ["Perusahaan", String(d.stats.companies)],
    ["Dokumen", String(d.stats.documents)],
    ["Data Issues", String(d.stats.issues)],
    ["Kelengkapan Dok.", d.stats.completePct + "%"],
  ];
  const cw = (W - M * 2 - 3 * 12) / 4;
  cards.forEach((c, i) => {
    const cx = M + i * (cw + 12);
    pdf.setDrawColor(226, 231, 239); pdf.setFillColor(246, 248, 251);
    pdf.roundedRect(cx, y, cw, 54, 6, 6, "FD");
    pdf.setFont("helvetica", "bold"); pdf.setFontSize(20); pdf.setTextColor(20, 41, 74);
    pdf.text(c[1], cx + 12, y + 26);
    pdf.setFont("helvetica", "normal"); pdf.setFontSize(9); pdf.setTextColor(100, 110, 122);
    pdf.text(c[0], cx + 12, y + 43);
  });
  y += 54 + 26;

  // Daftar perusahaan
  pdf.setFont("helvetica", "bold"); pdf.setFontSize(12); pdf.setTextColor(20, 30, 40);
  pdf.text("Perusahaan dalam grup", M, y); y += 8;
  pdf.setDrawColor(226, 231, 239); pdf.line(M, y, W - M, y); y += 16;
  pdf.setFontSize(10);
  for (const co of d.companies) {
    if (y > H - 120) { pdf.addPage(); y = M; }
    pdf.setFont("helvetica", "bold"); pdf.setTextColor(20, 30, 40);
    pdf.text(co.name, M, y);
    const stTxt = co.status === "red" ? "Perhatian" : co.status === "yellow" ? "Tinjau" : "OK";
    const stCol: [number, number, number] = co.status === "red" ? [176, 36, 79] : co.status === "yellow" ? [154, 106, 0] : [47, 122, 76];
    pdf.setFont("helvetica", "bold"); pdf.setTextColor(...stCol);
    pdf.text(stTxt, W - M, y, { align: "right" });
    y += 14;
    pdf.setFont("helvetica", "normal"); pdf.setTextColor(110, 118, 128); pdf.setFontSize(9.5);
    pdf.text(`${co.kedudukan} · ${co.shareholders} pemegang saham · ${co.docsMissing === 0 ? "dokumen lengkap" : co.docsMissing + " dokumen kurang"}`, M + 10, y);
    y += 18; pdf.setFontSize(10);
  }

  // UBO
  if (y > H - 120) { pdf.addPage(); y = M; }
  y += 8;
  pdf.setFont("helvetica", "bold"); pdf.setFontSize(12); pdf.setTextColor(20, 30, 40);
  pdf.text("Kandidat Ultimate Beneficial Owner (>25%)", M, y); y += 8;
  pdf.setDrawColor(226, 231, 239); pdf.line(M, y, W - M, y); y += 16;
  pdf.setFont("helvetica", "normal"); pdf.setFontSize(10.5); pdf.setTextColor(45, 52, 62);
  pdf.text(d.ubo.length ? d.ubo.join(", ") : "Tidak ada kandidat UBO di atas 25% pada langkah ini.", M, y, { maxWidth: W - M * 2 });
  y += 26;

  pdf.setFont("helvetica", "italic"); pdf.setFontSize(9); pdf.setTextColor(140, 148, 158);
  pdf.text("Ringkasan ini bersifat asistif untuk membantu peninjauan konsultan dan bukan kesimpulan/legal final.", M, y, { maxWidth: W - M * 2 });

  pdfBrandFooter(pdf, W, H, M);
  pdf.save(`strukturku-${slug(d.group)}-ringkasan-klien.pdf`);
}

export interface ActivityRow { waktu: string; pengguna: string; email: string; peran: string; aksi: string; objek: string; grup: string; }

export async function exportActivityLogPDF(rows: ActivityRow[], opts?: { filterLabel?: string }): Promise<void> {
  const { jsPDF } = await import("jspdf");
  const pdf = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4", compress: true });
  const W = pdf.internal.pageSize.getWidth();
  const H = pdf.internal.pageSize.getHeight();
  const M = 40;
  let y = pdfBrandHeader(pdf, W, M, { title: "Log Aktivitas" });
  if (opts?.filterLabel) {
    pdf.setFont("helvetica", "normal"); pdf.setFontSize(9.5); pdf.setTextColor(110, 118, 128);
    pdf.text(opts.filterLabel, M, y); y += 16;
  }
  const cols = [
    { k: "waktu", w: 105, t: "Waktu" },
    { k: "pengguna", w: 185, t: "Pengguna" },
    { k: "peran", w: 60, t: "Peran" },
    { k: "aksi", w: 110, t: "Aksi" },
    { k: "objek", w: 165, t: "Objek" },
    { k: "grup", w: 120, t: "Grup" },
  ];
  const xs: number[] = []; let cx = M; for (const c of cols) { xs.push(cx); cx += c.w; }
  const drawHead = () => {
    pdf.setFillColor(246, 248, 251); pdf.rect(M, y, W - M * 2, 22, "F");
    pdf.setFont("helvetica", "bold"); pdf.setFontSize(9); pdf.setTextColor(90, 100, 112);
    cols.forEach((c, i) => pdf.text(c.t, xs[i] + 4, y + 15));
    y += 22;
  };
  drawHead();
  for (const r of rows) {
    if (y > H - 46) { pdf.addPage(); y = M; drawHead(); }
    pdf.setFont("helvetica", "normal"); pdf.setFontSize(9); pdf.setTextColor(40, 52, 68);
    cols.forEach((c, i) => {
      const val = String((r as any)[c.k] || "");
      const line = pdf.splitTextToSize(val, c.w - 8)[0] || "";
      pdf.text(line, xs[i] + 4, y + 13);
    });
    if (r.email) {
      pdf.setFontSize(7.5); pdf.setTextColor(140, 148, 158);
      pdf.text(pdf.splitTextToSize(r.email, cols[1].w - 8)[0] || "", xs[1] + 4, y + 22);
    }
    pdf.setDrawColor(232, 236, 242); pdf.line(M, y + 27, W - M, y + 27);
    y += 30;
  }
  pdfBrandFooter(pdf, W, H, M, "log audit");
  pdf.save("strukturku-log-aktivitas.pdf");
}

export async function exportChart(kind: "png" | "pdf", opts: ExportOpts & { fileSuffix?: string }): Promise<void> {
  const { svg, w, h } = buildSVG(opts);
  const scale = kind === "pdf" ? 2 : 2.5;
  const dataUrl = await rasterize(svg, w, h, scale);
  const base = `strukturku-${slug(opts.group)}${opts.fileSuffix ? "-" + opts.fileSuffix : ""}`;
  if (kind === "png") {
    triggerDownload(dataUrl, `${base}.png`);
    return;
  }
  const { jsPDF } = await import("jspdf");
  const pdf = new jsPDF({ orientation: w >= h ? "landscape" : "portrait", unit: "px", format: [w, h], compress: true });
  pdf.addImage(dataUrl, "PNG", 0, 0, w, h);
  pdf.save(`${base}.pdf`);
}

// Ambil satu halaman (raster) dari scene yang sedang tampil di DOM.
export async function captureScene(opts: ExportOpts): Promise<{ dataUrl: string; w: number; h: number }> {
  const { svg, w, h } = buildSVG(opts);
  const dataUrl = await rasterize(svg, w, h, 2);
  return { dataUrl, w, h };
}

// Gabungkan beberapa halaman (tiap langkah) menjadi satu PDF, dengan SAMPUL + DAFTAR ISI.
export async function savePagesPDF(
  pages: { dataUrl: string; w: number; h: number }[],
  group: string,
  stepLabels?: string[],
  opts?: { filenameSuffix?: string; subtitle?: string; tocTitle?: string }
): Promise<void> {
  if (!pages.length) return;
  const { jsPDF } = await import("jspdf");
  const first = pages[0];
  const cw = first.w, ch = first.h;
  const pdf = new jsPDF({ orientation: cw >= ch ? "landscape" : "portrait", unit: "px", format: [cw, ch], compress: true });
  const t = readTheme();
  const now = new Date().toLocaleString("id-ID", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  const subtitle = opts?.subtitle ?? "Laporan Struktur Kepemilikan — Tiap Langkah Perubahan";
  const tocTitle = opts?.tocTitle ?? "Daftar isi";
  const filenameSuffix = opts?.filenameSuffix ?? "semua-langkah";

  // --- Halaman SAMPUL ---
  pdf.setFillColor(t.page || "#ffffff"); pdf.rect(0, 0, cw, ch, "F");
  const M = 60;
  let y = Math.max(120, ch * 0.22);
  pdf.setTextColor(t.ink || "#101418");
  pdf.setFont("times", "bold"); pdf.setFontSize(40);
  pdf.text("STRUKTURKU", M, y); y += 34;
  pdf.setFont("helvetica", "normal"); pdf.setFontSize(16); pdf.setTextColor(90, 100, 110);
  pdf.text(subtitle, M, y); y += 40;
  pdf.setFontSize(14); pdf.setTextColor(t.ink || "#101418");
  pdf.setFont("helvetica", "bold"); pdf.text(`Grup klien: ${group}`, M, y); y += 22;
  pdf.setFont("helvetica", "normal"); pdf.setFontSize(11.5); pdf.setTextColor(120, 128, 138);
  pdf.text(`Dihasilkan: ${now} • STRUKTURKU by Abadi Premium Consultant`, M, y); y += 34;

  if (stepLabels && stepLabels.length) {
    pdf.setFont("helvetica", "bold"); pdf.setFontSize(13); pdf.setTextColor(t.ink || "#101418");
    pdf.text(tocTitle, M, y); y += 20;
    pdf.setFont("helvetica", "normal"); pdf.setFontSize(11); pdf.setTextColor(70, 78, 88);
    for (let i = 0; i < stepLabels.length; i++) {
      if (y > ch - 60) break; // sampul cukup satu halaman
      const wrapped = pdf.splitTextToSize(`Hal. ${i + 2} — ${stepLabels[i]}`, cw - M * 2);
      pdf.text(wrapped, M, y);
      y += 15 * wrapped.length + 2;
    }
  }

  // --- Halaman isi (tiap langkah) ---
  for (let i = 0; i < pages.length; i++) {
    const p = pages[i];
    pdf.addPage([p.w, p.h], p.w >= p.h ? "landscape" : "portrait");
    pdf.addImage(p.dataUrl, "PNG", 0, 0, p.w, p.h);
  }
  pdf.save(`strukturku-${slug(group)}-${filenameSuffix}.pdf`);
}

// ---------------- Ekspor checklist kelengkapan dokumen ----------------
export interface DocChecklistItem { label: string; status: "Ada" | "Belum ada"; source: string; note: string; url: string; }
export interface DocChecklistRow { company: string; items: DocChecklistItem[]; }

export function exportDocChecklistCSV(rows: DocChecklistRow[], group: string): void {
  const esc = (s: string) => `"${(s || "").replace(/"/g, '""')}"`;
  const lines = ["PT,Dokumen,Status,Sumber,Catatan,Link"];
  for (const r of rows) {
    if (!r.items.length) { lines.push([r.company, "—", "Belum ada", "", "", ""].map(esc).join(",")); continue; }
    for (const it of r.items) {
      lines.push([r.company, it.label, it.status, it.source, it.note, it.url].map(esc).join(","));
    }
  }
  const csv = "\uFEFF" + lines.join("\r\n"); // BOM agar Excel baca UTF-8
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  triggerDownload(url, `strukturku-${slug(group)}-kelengkapan-dokumen.csv`);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export async function exportDocChecklistPDF(rows: DocChecklistRow[], group: string): Promise<void> {
  const { jsPDF } = await import("jspdf");
  const pdf = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4", compress: true });
  const W = pdf.internal.pageSize.getWidth();
  const H = pdf.internal.pageSize.getHeight();
  const M = 40;
  let y = M;

  const ensure = (need: number) => {
    if (y + need > H - M) { pdf.addPage(); y = M; }
  };

  y = pdfBrandHeader(pdf, W, M, { title: "Kelengkapan Dokumen", group });
  pdf.setFont("helvetica", "normal"); pdf.setFontSize(11); pdf.setTextColor(90, 100, 110);
  pdf.text(`Syarat minimal tiap PT: Akta Pendirian, Akta Perubahan (jika ada), NIB, NPWP.`, M, y); y += 18;
  pdf.setDrawColor(226, 231, 239); pdf.line(M, y, W - M, y); y += 16;

  for (const r of rows) {
    const missing = r.items.filter((i) => i.status === "Belum ada").length;
    ensure(28);
    pdf.setFont("helvetica", "bold"); pdf.setFontSize(12.5); pdf.setTextColor(20, 30, 40);
    pdf.text(r.company, M, y);
    pdf.setFont("helvetica", "bold"); pdf.setFontSize(10);
    if (missing === 0) { pdf.setTextColor(30, 130, 80); pdf.text("LENGKAP", W - M, y, { align: "right" }); }
    else { pdf.setTextColor(190, 60, 50); pdf.text(`KURANG ${missing}`, W - M, y, { align: "right" }); }
    y += 15;
    pdf.setFont("helvetica", "normal"); pdf.setFontSize(10);
    if (!r.items.length) {
      ensure(14); pdf.setTextColor(120, 128, 138); pdf.text("• Belum ada dokumen tercatat", M + 10, y); y += 14;
    }
    for (const it of r.items) {
      ensure(15);
      const ok = it.status === "Ada";
      pdf.setTextColor(ok ? 30 : 190, ok ? 130 : 60, ok ? 80 : 50);
      pdf.text(ok ? "✓" : "✗", M + 10, y);
      pdf.setTextColor(45, 52, 62);
      const meta = [it.source, it.note].filter(Boolean).join(" — ");
      const line = it.label + (meta ? `  (${meta})` : "");
      const wrapped = pdf.splitTextToSize(line, W - M * 2 - 30);
      pdf.text(wrapped, M + 26, y);
      y += 13 * wrapped.length + 2;
    }
    y += 8;
  }

  pdf.setFontSize(9); pdf.setTextColor(140, 148, 158);
  pdfBrandFooter(pdf, W, H, M);
  pdf.save(`strukturku-${slug(group)}-kelengkapan-dokumen.pdf`);
}


// ---------------- Ringkasan UBO (Ultimate Beneficial Owner) ----------------
export interface UBOHolding { company: string; pct: string; over25: boolean; }
export interface UBORow { name: string; isUBO: boolean; holdings: UBOHolding[]; }

export async function exportUBOReport(rows: UBORow[], group: string): Promise<void> {
  const { jsPDF } = await import("jspdf");
  const pdf = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4", compress: true });
  const W = pdf.internal.pageSize.getWidth();
  const H = pdf.internal.pageSize.getHeight();
  const M = 40;
  let y = M;
  const ensure = (need: number) => { if (y + need > H - M) { pdf.addPage(); y = M; } };

  y = pdfBrandHeader(pdf, W, M, { title: "Ringkasan UBO", group });
  pdf.setFont("helvetica", "normal"); pdf.setFontSize(11); pdf.setTextColor(90, 100, 110);
  pdf.text(`Kandidat UBO (Ultimate Beneficial Owner) = kepemilikan efektif di atas 25%.`, M, y); y += 14;
  pdf.text(`Persentase = kepemilikan efektif (menembus rantai penyertaan) per orang.`, M, y); y += 16;
  pdf.setDrawColor(210, 214, 220); pdf.line(M, y, W - M, y); y += 18;

  const ubos = rows.filter((r) => r.isUBO);
  ensure(16);
  pdf.setFont("helvetica", "bold"); pdf.setFontSize(11.5); pdf.setTextColor(20, 30, 40);
  pdf.text(`Kandidat UBO: ${ubos.length ? ubos.map((u) => u.name).join(", ") : "—"}`, M, y);
  y += 20;

  for (const r of rows) {
    ensure(26);
    pdf.setFont("helvetica", "bold"); pdf.setFontSize(12.5); pdf.setTextColor(20, 30, 40);
    pdf.text(r.name, M, y);
    pdf.setFontSize(10);
    if (r.isUBO) { pdf.setTextColor(176, 36, 79); pdf.text("KANDIDAT UBO", W - M, y, { align: "right" }); }
    else { pdf.setTextColor(120, 128, 138); pdf.text("bukan UBO", W - M, y, { align: "right" }); }
    y += 15;
    pdf.setFont("helvetica", "normal"); pdf.setFontSize(10);
    if (!r.holdings.length) { ensure(14); pdf.setTextColor(120, 128, 138); pdf.text("• Tidak ada kepemilikan efektif", M + 12, y); y += 14; }
    for (const h of r.holdings) {
      ensure(14);
      pdf.setTextColor(h.over25 ? 176 : 70, h.over25 ? 36 : 78, h.over25 ? 79 : 88);
      pdf.setFont("helvetica", h.over25 ? "bold" : "normal");
      pdf.text(`• ${h.company}: ${h.pct}${h.over25 ? "  (>25%)" : ""}`, M + 12, y);
      y += 14;
    }
    y += 8;
  }

  pdf.setFont("helvetica", "normal"); pdf.setFontSize(9); pdf.setTextColor(140, 148, 158);
  pdfBrandFooter(pdf, W, H, M, "bukan nasihat hukum");
  pdf.save(`strukturku-${slug(group)}-ringkasan-ubo.pdf`);
}
